import { formatUkDateSafe, isSetDate } from "../../../Helpers/Dates";
import { IDropDownListItem } from "../../../Types/DropDownList/IDropDownListItem";
import { getProductTypeName, ProductType } from "../../../Types/ProductType";
import { IProductQuestionnaire } from "../../../Types/Products/IProductQuestionnaire";
import { ICriteriaItem } from "../../../Types/Products/ICriteriaItemData";
import {
  AreaExportData,
  EXPORT_COLS,
  ExportFieldVisibility,
  ExportOptions,
  fullExportFieldVisibility,
} from "../types";
import {
  additionalInfoEntries,
  criteriaGradeDisplay,
  developDropdownContent,
  developPersonContent,
  developPicklistContent,
  fieldsInExportOrder,
  findGradeOptionForStoredValue,
  introductionText,
  tagsString,
} from "./exportHelpers";

/** UTF-8 self-contained HTML; safe for insertion into `<body>` and text nodes via escaping. */
function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatExportTimestamp(): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
}

/** Escape then preserve line breaks from rich text-ish content. */
function bodyHtml(text: string): string {
  return escapeHtml(text).replace(/\r\n/g, "\n").split("\n").join("<br>");
}

function displayVal(value: string): string {
  const t = value.trim();
  return t.length > 0 ? bodyHtml(t) : "—";
}

function dlRow(dt: string, ddHtml: string): string {
  return `<div class="dl-row"><dt>${escapeHtml(dt)}</dt><dd>${ddHtml}</dd></div>`;
}

/** Sort profile options — higher `progressValue` first (typically “best” tier at top). */
function gradeOptionsInProfileOrder(opts: IDropDownListItem[]): IDropDownListItem[] {
  return [...opts].sort((a, b) => {
    const pb = b.progressValue ?? -1;
    const pa = a.progressValue ?? -1;
    if (pb !== pa) return pb - pa;
    return 0;
  });
}

function criteriaMatchesGradeOption(c: ICriteriaItem, opt: IDropDownListItem): boolean {
  const raw = c.grade;
  if (raw === undefined || raw === null) return false;
  const g = String(raw).trim();
  if (!g.length) return false;
  if (opt.value !== undefined && String(opt.value) === g) return true;
  if (opt.text === g) return true;
  return false;
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return `rgba(229, 231, 235, ${alpha})`;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function barColourAndTint(colour: string | undefined): { bar: string; tint: string } {
  const bar = normalizeHexColor(colour) ?? "#9ca3af";
  return { bar, tint: hexToRgba(bar, 0.14) };
}

function normalizeHexColor(raw: string | undefined): string | undefined {
  const t = raw?.trim();
  if (!t) return undefined;
  if (/^#[0-9a-fA-F]{6}$/i.test(t)) return t.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/i.test(t)) return `#${t.toLowerCase()}`;
  return undefined;
}

const MAX_GRADE_DOTS = 40;

/** Coloured grading key (Ofsted-style) using this questionnaire’s `gradeOptions` + criteria counts. */
function buildGradeKeyHtml(
  q: IProductQuestionnaire,
  vis: ExportFieldVisibility
): string {
  if (!vis.Grade) return "";
  const opts = q.gradeOptions ?? [];
  if (!opts.length) return "";

  const ordered = gradeOptionsInProfileOrder(opts);
  const counts = new Map<IDropDownListItem, number>();
  for (const o of ordered) counts.set(o, 0);

  for (const section of q.questionnaireSections ?? []) {
    for (const c of section.criteria ?? []) {
      const match = ordered.find((o) => criteriaMatchesGradeOption(c, o));
      if (match) counts.set(match, (counts.get(match) ?? 0) + 1);
    }
  }

  const overallOpt = findGradeOptionForStoredValue(q, q.overallGrade);

  const rows = ordered.map((opt) => {
    const n = counts.get(opt) ?? 0;
    const { bar, tint } = barColourAndTint(opt.colour);
    const label = escapeHtml(opt.text ?? "");
    const dotsToShow = Math.min(n, MAX_GRADE_DOTS);
    const dots = Array.from(
      { length: dotsToShow },
      () =>
        `<span class="grade-key-dot" style="background:${bar};-webkit-print-color-adjust:exact;print-color-adjust:exact" aria-hidden="true"></span>`
    ).join("");
    const overflow =
      n > MAX_GRADE_DOTS
        ? `<span class="grade-key-more" title="${escapeHtml(String(n))} criteria">(+${n - MAX_GRADE_DOTS})</span>`
        : "";
    const isCurrent =
      overallOpt !== undefined &&
      overallOpt.text === opt.text &&
      String(overallOpt.value ?? "") === String(opt.value ?? "");
    const rowClass = `grade-key-row${isCurrent ? " grade-key-row--current" : ""}`;
    return `<div class="${rowClass}">
  <div class="grade-key-cells" style="background:${tint};border-left:10px solid ${bar};-webkit-print-color-adjust:exact;print-color-adjust:exact">
    <span class="grade-key-label">${label}</span>
    <span class="grade-key-dots">${dots}${overflow}</span>
  </div>
</div>`;
  }).join("\n");

  return `<aside class="grade-key" aria-label="Grading scale">
<h2 class="grade-key-heading">Grading scale</h2>
<p class="grade-key-note">Colours and labels are defined by this diagnostic&apos;s grade profile. Dots show how many criteria have each grade.</p>
<div class="grade-key-table">${rows}</div>
</aside>`;
}

/** Field title / content blocks honour export column visibility (mirrors docx). */
function fieldBlocksHtml(
  fieldTitle: string,
  content: string,
  vis: ExportFieldVisibility
): string {
  const showTitle = vis["Field title"];
  const showContent = vis["Field content"];
  if (!showTitle && !showContent) return "";
  const trimmed = content.trim();
  const body = trimmed.length > 0 ? bodyHtml(trimmed) : "—";
  const parts: string[] = [];
  if (showTitle) {
    parts.push(`<p class="field-title">${escapeHtml(fieldTitle)}</p>`);
  }
  if (showContent) {
    parts.push(`<div class="field-content">${body}</div>`);
  }
  return `<div class="field-block">${parts.join("\n")}</div>`;
}

function gradeFieldBlockHtml(
  q: IProductQuestionnaire,
  c: ICriteriaItem,
  vis: ExportFieldVisibility
): string {
  const showTitle = vis["Field title"];
  const showContent = vis["Field content"];
  if (!showTitle && !showContent) return "";

  const label = criteriaGradeDisplay(q, c);
  const trimmed = label.trim();
  const hasValue = trimmed.length > 0 && trimmed !== "Not Set";
  const display = hasValue ? escapeHtml(trimmed) : "—";
  const opt = hasValue ? findGradeOptionForStoredValue(q, c.grade) : undefined;
  const { bar } = barColourAndTint(opt?.colour);

  const dot =
    hasValue && opt
      ? `<span class="criteria-grade-dot" style="background:${bar};-webkit-print-color-adjust:exact;print-color-adjust:exact" aria-hidden="true"></span>`
      : "";

  const parts: string[] = [];
  if (showTitle) {
    parts.push(`<p class="field-title">Grade</p>`);
  }
  if (showContent) {
    parts.push(
      `<div class="field-content field-content--grade">${dot}<span class="field-content-grade-text">${display}</span></div>`
    );
  }

  return `<div class="field-block field-block--grade">${parts.join("\n")}</div>`;
}

function criteriaMetaGridHtml(blocks: string[]): string {
  const items = blocks.filter(Boolean);
  if (items.length === 0) return "";
  return `<div class="criteria-meta-grid">${items.join("\n")}</div>`;
}

/** Develop optional fields and grade/tags sit on separate rows (matches diagnostic UI). */
function criteriaMetaGridsHtml(
  developBlocks: string[],
  gradeTagsBlocks: string[]
): string {
  const developGrid = criteriaMetaGridHtml(developBlocks);
  const gradeTagsGrid = criteriaMetaGridHtml(gradeTagsBlocks);
  if (!developGrid && !gradeTagsGrid) return "";

  const sections: string[] = [];
  if (developGrid) {
    sections.push(`<div class="criteria-meta-section">${developGrid}</div>`);
  }
  if (gradeTagsGrid) {
    const gradeTagsSectionClass = developGrid
      ? "criteria-meta-section criteria-meta-section--grade-tags"
      : "criteria-meta-section";
    sections.push(
      `<div class="${gradeTagsSectionClass}">${gradeTagsGrid}</div>`
    );
  }

  return `<div class="criteria-meta-grids">${sections.join("\n")}</div>`;
}

function criteriaInnerHtml(
  area: AreaExportData,
  c: ICriteriaItem,
  vis: ExportFieldVisibility,
  evidence: string
): string {
  const bodyParts: string[] = [];
  const developMetaBlocks: string[] = [];
  const gradeTagsBlocks: string[] = [];
  const q = area.questionnaire;
  const isDevelop = q.productType === ProductType.Develop;
  const personTitle = (q.personName?.trim() || "Person") as string;
  const picklistTitle = (q.picklistName?.trim() || "Picklist") as string;
  const dropdownTitle = (q.dropdownName?.trim() || "Dropdown") as string;
  const tags = tagsString(c);

  const templateFieldBlocks: string[] = [];
  const fields = fieldsInExportOrder(c.fields);
  for (const f of fields) {
    const fieldTitle = f.title ? `${f.title}${f.mandatory ? " *" : ""}` : "";
    const content = f.content ?? "";
    if (!fieldTitle && !content.trim()) continue;
    templateFieldBlocks.push(
      fieldBlocksHtml(
        fieldTitle.length > 0 ? fieldTitle : "(No title)",
        content,
        vis
      )
    );
  }

  if (templateFieldBlocks.length > 0) {
    bodyParts.push(
      `<div class="criteria-fields">${templateFieldBlocks.join("\n")}</div>`
    );
  }

  if (isDevelop) {
    if (q.personShown) {
      developMetaBlocks.push(
        fieldBlocksHtml(personTitle, developPersonContent(c), vis)
      );
    }
    if (q.dropdownShown) {
      developMetaBlocks.push(
        fieldBlocksHtml(dropdownTitle, developDropdownContent(c), vis)
      );
    }
    if (q.picklistShown) {
      developMetaBlocks.push(
        fieldBlocksHtml(picklistTitle, developPicklistContent(c), vis)
      );
    }
  }

  if (vis.Grade) {
    gradeTagsBlocks.push(gradeFieldBlockHtml(q, c, vis));
  }

  if (vis.Tags) {
    gradeTagsBlocks.push(fieldBlocksHtml("Tags", tags, vis));
  }

  const metaGrids = criteriaMetaGridsHtml(developMetaBlocks, gradeTagsBlocks);
  if (metaGrids) {
    bodyParts.push(metaGrids);
  }

  if (vis["Linked evidence files"] && evidence.length > 0) {
    bodyParts.push(
      `<div class="criteria-evidence">${dlRow("Linked evidence files", displayVal(evidence))}</div>`
    );
  }

  const body = bodyParts.filter(Boolean).join("\n");
  return body ? `<div class="criteria-body">${body}</div>` : "";
}

function wrapDetails(
  open: boolean,
  summaryClass: string,
  summaryInnerHtml: string,
  bodyHtmlStr: string
): string {
  const openAttr = open ? " open" : "";
  return `<details${openAttr} class="report-details"><summary class="${summaryClass}"><span class="summary-inner">${summaryInnerHtml}</span></summary><div class="details-body">${bodyHtmlStr}</div></details>`;
}

function wrapSection(
  sectionTitle: string,
  showSectionTitle: boolean,
  inner: string
): string {
  if (!showSectionTitle) {
    return `<div class="section-flat">${inner}</div>`;
  }
  const label =
    sectionTitle.length > 0 ? sectionTitle : "Untitled";
  const summary = `<span class="section-label">${escapeHtml(label)}</span>`;
  return wrapDetails(true, "summary-section", summary, inner);
}

function wrapCriteria(
  criteriaTitle: string,
  showCriteriaTitle: boolean,
  inner: string
): string {
  if (!showCriteriaTitle) {
    return `<div class="criteria-flat">${inner}</div>`;
  }
  const label =
    criteriaTitle.length > 0 ? criteriaTitle : "Untitled criteria";
  const summary = `<span class="criteria-title-line"><span class="criteria-label">${escapeHtml(
    label
  )}</span></span>`;
  return wrapDetails(true, "summary-criteria", summary, inner);
}

function buildAreaHtml(area: AreaExportData, vis: ExportFieldVisibility): string {
  const q = area.questionnaire;
  const title = q.title ?? "";
  const templateType = getProductTypeName(q.productType);
  const due = formatUkDateSafe(area.displayInfo?.dueDate);
  const owner = area.displayInfo?.ownerName ?? "";
  const completionRaw = area.displayInfo?.completedDate;
  const completion = formatUkDateSafe(
    isSetDate(completionRaw) ? completionRaw : undefined
  );

  const metaBits: string[] = [];
  if (vis.Owner && owner.trim())
    metaBits.push(`<span class="meta-chip">Owner: ${escapeHtml(owner)}</span>`);
  if (vis["Due date"]) metaBits.push(`<span class="meta-chip">Due: ${escapeHtml(due)}</span>`);

  const titleHtml = vis.Title
    ? escapeHtml(title.length > 0 ? title : "Untitled")
    : "Diagnostic";

  const summaryTop = `<span class="area-title-text">${titleHtml}</span>${
    metaBits.length
      ? `<span class="area-meta">${metaBits.join("")}</span>`
      : ""
  }`;

  const areaHeaderDl: string[] = [];
  if (vis["Type of template"]) {
    areaHeaderDl.push(dlRow("Type of template", displayVal(templateType)));
  }
  if (vis["Completion date"]) {
    areaHeaderDl.push(dlRow("Completion date", displayVal(completion)));
  }
  if (vis.Owner && owner.trim() && !metaBits.some((m) => m.includes("Owner")))
    areaHeaderDl.push(dlRow("Owner", displayVal(owner)));
  if (vis["Due date"] && !metaBits.some((m) => m.includes("Due:")))
    areaHeaderDl.push(dlRow("Due date", displayVal(due)));

  const headerBlock =
    areaHeaderDl.length > 0
      ? `<div class="area-header-dl">${areaHeaderDl.join("")}</div>`
      : "";

  const intro = introductionText(q);
  const introBlock =
    vis.Introduction && intro
      ? `<section class="area-block area-block--intro">
  <h2 class="area-block-heading">Introduction</h2>
  <div class="field-content">${bodyHtml(intro)}</div>
</section>`
      : "";

  const additionalEntries = additionalInfoEntries(q);
  const additionalBlock =
    vis["Additional information"] && additionalEntries.length > 0
      ? `<section class="area-block area-block--additional">
  <h2 class="area-block-heading">Additional information</h2>
  <div class="area-additional-dl">${additionalEntries
    .map((e) =>
      dlRow(e.header.length > 0 ? e.header : "(No title)", displayVal(e.value))
    )
    .join("")}</div>
</section>`
      : "";

  const gradeKeyBlock = buildGradeKeyHtml(q, vis);

  const sectionParts: string[] = [];
  for (const section of q.questionnaireSections ?? []) {
    const sectionTitle = section.title ?? "";
    const criteriaParts: string[] = [];
    for (const c of section.criteria ?? []) {
      const criteriaTitle = c.title ?? "";
      const evidence = (area.evidenceByCriteriaId[c.id] ?? []).join("; ");
      const inner = criteriaInnerHtml(area, c, vis, evidence);
      if (!inner.trim()) continue;
      criteriaParts.push(
        wrapCriteria(criteriaTitle, vis.Criteria, inner)
      );
    }
    if (criteriaParts.length === 0) continue;
    sectionParts.push(
      wrapSection(sectionTitle, vis.Section, criteriaParts.join("\n"))
    );
  }

  const body = `${headerBlock}${introBlock}${additionalBlock}${gradeKeyBlock}${sectionParts.join("\n")}`;
  return wrapDetails(true, "summary-area", summaryTop, body);
}

const REPORT_STYLES = `
  :root {
    --navy: rgb(36, 57, 79);
    --grey-mid: #6b7280;
    --grey-light: #e5e7eb;
    --bg: #f9fafb;
    --report-max-width: 960px;
    --report-gutter: 1rem;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    line-height: 1.5;
    color: #111827;
    background: var(--bg);
  }
  .report-page-header {
    background: var(--navy);
    color: #fff;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1rem;
  }
  .report-page-header h1 {
    margin: 0 0 0.35rem 0;
    font-size: 1.35rem;
    font-weight: 700;
  }
  .report-page-header .sub {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.9;
  }
  .report-shell {
    position: relative;
    max-width: var(--report-max-width);
    margin: 0 auto 2rem;
    padding: 4.5rem var(--report-gutter) 2rem;
  }
  .report-pdf-btn {
    position: absolute;
    top: 0;
    right: var(--report-gutter);
    z-index: 10;
    font: inherit;
    font-size: 1.15rem;
    font-weight: 700;
    cursor: pointer;
    padding: 1.1rem 2rem;
    min-width: 12rem;
    border: 2px solid var(--navy);
    border-radius: 10px;
    background: #fff;
    color: var(--navy);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    animation: report-pdf-btn-pulse 1.25s ease-in-out 4;
  }
  @keyframes report-pdf-btn-pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    }
    50% {
      transform: scale(1.07);
      box-shadow: 0 10px 36px rgba(36, 57, 79, 0.42);
    }
  }
  .report-pdf-btn:hover {
    background: var(--navy);
    color: #fff;
    transform: scale(1.02);
  }
  .report-pdf-btn:focus-visible {
    outline: 3px solid var(--navy);
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    .report-pdf-btn { animation: none; }
  }
  .report-root {
    margin: 0;
    padding: 0;
  }
  .report-details { margin-bottom: 0.5rem; }
  .report-details > summary {
    list-style: none;
    cursor: pointer;
    user-select: none;
  }
  .report-details > summary::-webkit-details-marker { display: none; }
  .summary-inner { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 0.65rem 1rem; }
  .summary-area {
    background: var(--navy);
    color: #fff;
    padding: 0.85rem 1rem;
    border-radius: 4px 4px 0 0;
    font-weight: 600;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .summary-area .summary-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .summary-area .area-title-text { font-size: 1.1rem; line-height: 1.35; }
  .summary-area .area-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    font-weight: 400;
    font-size: 0.88rem;
    opacity: 0.95;
    width: 100%;
  }
  .meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    white-space: nowrap;
  }
  .summary-section {
    background: #4b5563;
    color: #fff;
    padding: 0.65rem 0.9rem;
    margin-top: 0.75rem;
    border-radius: 4px 4px 0 0;
    font-size: 0.98rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .summary-criteria {
    background: var(--grey-light);
    color: #111827;
    padding: 0.55rem 0.85rem;
    margin-top: 0.5rem;
    border-radius: 4px;
    font-size: 0.95rem;
    border: 1px solid #d1d5db;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .summary-criteria .summary-inner {
    width: 100%;
  }
  .criteria-title-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
  }
  .criteria-label { flex: 1 1 auto; min-width: 0; }
  .criteria-grade-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    max-width: min(100%, 22rem);
    padding: 0.3rem 0.65rem;
    border-radius: 4px;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.35;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .criteria-grade-text { overflow-wrap: anywhere; }
  .criteria-grade-dot,
  .grade-key-dot {
    flex-shrink: 0;
    display: inline-block;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid #000;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .details-body {
    padding: 1rem 1.25rem 1.35rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 4px 4px;
  }
  .criteria-body {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
  }
  .criteria-fields {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .criteria-meta-grids {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .criteria-meta-section--grade-tags {
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
  }
  .criteria-meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: 0.75rem 1rem;
    align-items: stretch;
  }
  .criteria-meta-grid .field-block {
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 0.65rem 0.85rem;
    margin: 0;
    border: 1px solid #f3f4f6;
    border-radius: 6px;
    background: #fafafa;
  }
  .criteria-meta-grid .field-title {
    margin-bottom: 0.35rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: var(--grey-mid);
  }
  .criteria-meta-grid .field-content {
    font-size: 0.9rem;
    line-height: 1.45;
  }
  .criteria-meta-grid .field-block--grade .field-content--grade {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    color: #111827;
  }
  .criteria-meta-grid .field-block--grade .criteria-grade-dot {
    flex-shrink: 0;
  }
  .criteria-meta-grid .field-block--grade .field-content-grade-text {
    flex: 1;
    min-width: 0;
  }
  .criteria-evidence {
    padding-top: 0.25rem;
    border-top: 1px solid #f3f4f6;
  }
  .criteria-evidence .dl-row {
    border-bottom: none;
    padding-bottom: 0;
  }
  .summary-area + .details-body { border-radius: 0 0 4px 4px; border-top: 1px solid #e5e7eb; }
  .section-flat, .criteria-flat { padding-left: 0.25rem; }
  .area-header-dl {
    margin-bottom: 1.35rem;
    padding: 0.85rem 1rem;
    background: #fff;
    border: 1px solid var(--grey-light);
    border-radius: 6px;
  }
  .area-block {
    margin-bottom: 1.25rem;
    padding: 0.85rem 1rem;
    background: #fff;
    border: 1px solid var(--grey-light);
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(15,23,42,0.05);
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .area-block-heading {
    margin: 0 0 0.65rem 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--navy);
  }
  .area-block .field-content {
    margin: 0;
    font-size: 0.92rem;
    line-height: 1.7;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  .area-additional-dl .dl-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .meta-grade-dot {
    display: inline-block;
    width: 0.62rem;
    height: 0.62rem;
    border-radius: 50%;
    border: 1px solid #111;
    margin-left: 0.35rem;
    vertical-align: middle;
    position: relative;
    top: -1px;
  }
  .grade-key {
    border: 1px solid var(--grey-light);
    border-radius: 6px;
    padding: 0.85rem 1rem;
    margin-bottom: 1.25rem;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15,23,42,0.05);
  }
  .grade-key-heading {
    margin: 0 0 0.35rem 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--navy);
  }
  .grade-key-note {
    margin: 0 0 0.85rem 0;
    font-size: 0.84rem;
    color: var(--grey-mid);
  }
  .grade-key-table { display: flex; flex-direction: column; gap: 6px; }
  .grade-key-row {
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid #e8eaed;
  }
  .grade-key-row--current {
    outline: 2px solid var(--navy);
    outline-offset: 2px;
  }
  .grade-key-cells {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
    padding: 0.5rem 0.85rem;
    min-height: 2.65rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    box-sizing: border-box;
  }
  .grade-key-label {
    font-weight: 600;
    font-size: 0.92rem;
  }
  .grade-key-dots {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    justify-content: flex-end;
  }
  .grade-key-more { font-size: 0.8rem; color: var(--grey-mid); font-weight: 600; margin-left: 2px; }
  .dl-row {
    display: grid;
    grid-template-columns: minmax(8rem, 14rem) 1fr;
    gap: 0.5rem 1.25rem;
    padding: 0.65rem 0;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.92rem;
    line-height: 1.55;
  }
  .dl-row:last-child { border-bottom: none; padding-bottom: 0; }
  .dl-row dt { color: var(--grey-mid); font-weight: 600; margin: 0; }
  .dl-row dd { margin: 0; overflow-wrap: anywhere; }
  .field-block {
    padding: 0.85rem 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .criteria-fields .field-block:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .field-title {
    font-weight: 600;
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    line-height: 1.4;
    color: var(--navy);
  }
  .field-content {
    margin: 0;
    font-size: 0.92rem;
    line-height: 1.7;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  @media print {
    .report-pdf-btn { display: none !important; }
    .report-shell { padding-top: 0; }
    body { background: #fff; }
    /* Keep title band colours in Save as PDF / print (otherwise browsers omit backgrounds). */
    .report-page-header,
    .summary-area,
    .summary-section,
    .summary-criteria,
    .grade-key-row,
    .grade-key-cells {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    summary.summary-area,
    summary.summary-area .summary-inner,
    summary.summary-area .area-title-text,
    summary.summary-area .area-meta {
      color: #fff !important;
    }
    summary.summary-criteria,
    summary.summary-criteria .criteria-label,
    summary.summary-criteria .criteria-title-line,
    summary.summary-criteria .criteria-grade-text {
      color: #111827 !important;
    }
    details.report-details > summary {
      cursor: default;
      page-break-after: avoid;
    }
    .summary-area .area-meta { opacity: 1; }
    details.report-details { display: block !important; }
    details.report-details > *:not(summary) {
      display: block !important;
    }
    .details-body { border: 1px solid #ccc; }
    details > summary::-webkit-details-marker { display: none; }
    details > summary::marker { content: ""; }
  }
`;

function buildDocumentHtml(areasHtml: string, pageTitle: string): string {
  const safeTitle = escapeHtml(pageTitle);
  const exported = escapeHtml(formatExportTimestamp());
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${safeTitle}</title>
<style>${REPORT_STYLES}</style>
</head>
<body>
<header class="report-page-header">
  <h1>${safeTitle}</h1>
  <p class="sub">Exported ${exported}</p>
</header>
<div class="report-shell">
  <button type="button" class="report-pdf-btn" id="download-pdf-btn">Download as PDF</button>
  <div class="report-root">${areasHtml}</div>
</div>
<script>
(function () {
  var btn = document.getElementById("download-pdf-btn");
  if (btn) btn.addEventListener("click", function () { window.print(); });
})();
</script>
</body>
</html>`;
}

export async function htmlExporter(
  areas: AreaExportData[],
  baseFilename: string,
  options?: ExportOptions
): Promise<Blob> {
  if (areas.length === 0) {
    throw new Error("No areas to export.");
  }

  const vis = fullExportFieldVisibility(options?.fieldVisibility);
  if (!EXPORT_COLS.some((k) => vis[k] === true)) {
    throw new Error("No export columns selected.");
  }

  const pageTitle =
    areas.length === 1
      ? areas[0].questionnaire.title?.trim() || baseFilename
      : baseFilename;

  const areasHtml = areas.map((a) => buildAreaHtml(a, vis)).join("\n");
  const html = buildDocumentHtml(areasHtml, pageTitle);
  return new Blob([html], { type: "text/html;charset=utf-8" });
}
